import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, UserCheck, UserPlus, LogIn, CheckCircle2, Users, Plus, Trash2, ShieldCheck, Eye, EyeOff, X, Calendar, History } from "lucide-react";
import api from "../lib/api";

const TOKEN_KEY = "sms_p_token";

export default function Wejscie() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [view, setView] = useState("login"); // login | register | hub | success | addChild | forgot
  const [successInfo, setSuccessInfo] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const reloadMe = useCallback(async () => {
    if (!token) { setMe(null); setView("login"); return; }
    try {
      const { data } = await api.get("/participants/me", { headers: { Authorization: `Bearer ${token}` } });
      setMe(data);
      setView("hub");
    } catch (e) {
      console.error("Auth error:", e);
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMe(null);
      setView("login");
    }
  }, [token]);

  useEffect(() => { reloadMe(); }, [reloadMe]);

  const onLoggedIn = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMe(null);
    setView("login");
    setSuccessInfo(null);
    if (view === "success") {
        navigate("/");
    }
  }, [view, navigate]);

  // auto-logout after success display
  useEffect(() => {
    if (view !== "success") return;
    const t = setTimeout(logout, 5000);
    return () => clearTimeout(t);
  }, [view, logout]);

  const handleCheckin = async (participantId) => {
    try {
      const { data } = await api.post("/checkins", { participant_id: participantId }, { headers });
      setSuccessInfo(data);
      setView("success");
    } catch (e) {
      alert(e.response?.data?.detail || "Błąd rejestracji wejścia");
    }
  };

  return (
    <div data-testid="wejscie-page" className="min-h-screen bg-[#2A2A2A] flex flex-col">
      <header className="border-b border-[#3a3a3a] bg-[#1F1F1F] px-5 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="wejscie-home-link">
          <img src="/logo.png" alt="Śląski Maker Space" className="h-10 w-auto"/>
          <span className="hidden md:flex flex-col leading-tight">
            <span className="font-mono-acc text-[10px] text-[#FFCC00] uppercase">Kiosk</span>
            <span className="font-display text-white text-sm uppercase">Rejestracja wejścia</span>
          </span>
        </Link>
        {me && (
          <button onClick={logout} data-testid="wejscie-logout" className="font-mono-acc text-xs uppercase text-white/60 hover:text-[#FFCC00]">
            Wyloguj
          </button>
        )}
      </header>

      <main className="flex-1 flex items-center justify-center p-5">
        <div className="w-full max-w-2xl">
          {view === "login" && <LoginCard onLoggedIn={onLoggedIn} switchToRegister={() => setView("register")} switchToForgot={() => setView("forgot")}/>}
          {view === "register" && <RegisterCard back={() => setView("login")}/>}
          {view === "forgot" && <ForgotPasswordCard back={() => setView("login")}/>}
          {view === "hub" && me && <Hub me={me} token={token} onCheckin={handleCheckin} onAddChild={() => setView("addChild")} reload={reloadMe}/>}
          {view === "addChild" && <AddChildCard headers={headers} back={() => { setView("hub"); reloadMe(); }}/>}
          {view === "success" && successInfo && <SuccessCard info={successInfo} onContinue={logout}/>}
        </div>
      </main>

      <footer className="border-t border-[#3a3a3a] bg-[#1F1F1F] px-5 md:px-10 py-4 text-center font-mono-acc text-[10px] text-white/40 uppercase">
        Sesja w tym terminalu zostanie automatycznie zakończona po rejestracji wejścia.
      </footer>
    </div>
  );
}

function LoginCard({ onLoggedIn, switchToRegister, switchToForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/participants/login", { email, password });
      onLoggedIn(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Błąd logowania");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 md:p-12" data-testid="wejscie-login-card">
      <div className="kicker mb-3">Wejście / Logowanie</div>
      <h1 className="font-display text-white text-3xl md:text-5xl uppercase mb-2">
        Witaj <span className="text-[#FFCC00]">w pracowni</span>
      </h1>
      <p className="text-white/60 mb-8 text-sm">Zaloguj się, aby zarejestrować swoją obecność. Pierwszy raz? Załóż konto.</p>

      <form onSubmit={submit} className="space-y-4" data-testid="wejscie-login-form" autoComplete="off">
        <Field label="Email" type="email" required value={email} onChange={setEmail} testid="wejscie-login-email" />
        <div>
          <Field label="Hasło" type="password" required value={password} onChange={setPassword} testid="wejscie-login-password" />
          <div className="mt-2 text-right">
            <button type="button" onClick={switchToForgot} className="font-mono-acc text-[10px] uppercase text-white/40 hover:text-[#FFCC00]">
              Zapomniałeś hasła?
            </button>
          </div>
        </div>
        {err && <div className="text-red-400 text-sm" data-testid="wejscie-login-error">{err}</div>}
        <button type="submit" disabled={loading} data-testid="wejscie-login-submit"
          className="w-full bg-[#FFCC00] text-[#2A2A2A] font-display py-4 btn-brutal disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <LogIn size={18}/> {loading ? "..." : "ZALOGUJ"}
        </button>
      </form>

      <div className="mt-8 pt-6 border-t border-[#3a3a3a] text-center">
        <p className="text-white/55 text-sm mb-3">Nie masz jeszcze konta?</p>
        <button onClick={switchToRegister} data-testid="wejscie-switch-register"
          className="font-mono-acc text-xs uppercase text-[#FFCC00] hover:underline inline-flex items-center gap-2">
          <UserPlus size={14}/> Załóż konto uczestnika
        </button>
      </div>
    </div>
  );
}

function RegisterCard({ back }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "", email: "", password: "", confirm_password: "", city: "",
    marketing_consent: false, terms_consent: false,
  });
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_consent) { setErr("Musisz zaakceptować regulamin."); return; }
    if (form.password !== form.confirm_password) { setErr("Hasła muszą być identyczne."); return; }
    setErr(""); setSuccess(""); setLoading(true);
    try {
      const { data } = await api.post("/participants/register", form);
      setSuccess(data.detail || "Zarejestrowano pomyślnie! Sprawdź e-mail, aby aktywować konto.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Błąd rejestracji");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 md:p-10" data-testid="wejscie-register-card">
      <button onClick={back} className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFCC00] mb-4 font-mono-acc text-xs uppercase" data-testid="wejscie-register-back">
        <ArrowLeft size={14}/> Wróć
      </button>

      {success ? (
        <div className="text-center py-10">
          <CheckCircle2 size={64} className="text-[#FFCC00] mx-auto mb-6"/>
          <h2 className="font-display text-white text-3xl uppercase mb-4">Sprawdź e-mail</h2>
          <p className="text-white/70 mb-8">{success}</p>
          <button onClick={back} className="bg-[#FFCC00] text-[#2A2A2A] font-display px-8 py-4 btn-brutal">
            POWRÓT DO LOGOWANIA
          </button>
        </div>
      ) : (
        <>
          <div className="kicker mb-3">Rejestracja konta — opiekun / dorosły</div>
          <h2 className="font-display text-white text-3xl uppercase mb-6">
            Załóż <span className="text-[#FFCC00]">profil</span>
          </h2>

          <form onSubmit={submit} className="space-y-4" data-testid="wejscie-register-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Imię" required value={form.first_name} onChange={(v) => upd("first_name", v)} testid="reg-firstname"/>
              <Field label="Nazwisko" required value={form.last_name} onChange={(v) => upd("last_name", v)} testid="reg-lastname"/>
              <Field label="Data urodzenia" type="date" required value={form.dob} onChange={(v) => upd("dob", v)} testid="reg-dob"/>
              <Field label="Miasto" value={form.city} onChange={(v) => upd("city", v)} testid="reg-city"/>
              <Field label="Email" type="email" required value={form.email} onChange={(v) => upd("email", v)} testid="reg-email"/>
              <Field label="Hasło (min 8 znaków)" type="password" required minLength={8} value={form.password} onChange={(v) => upd("password", v)} testid="reg-password"/>
              <Field label="Powtórz hasło" type="password" required value={form.confirm_password} onChange={(v) => upd("confirm_password", v)} testid="reg-confirm-password"/>
            </div>

            <div className="space-y-3 pt-2 border-t border-[#3a3a3a] mt-2">
              <Checkbox
                testid="reg-terms"
                checked={form.terms_consent}
                onChange={(v) => upd("terms_consent", v)}
                label={<>Oświadczam, że zapoznałem/am się z <Link to="/regulamin" target="_blank" className="text-[#FFCC00] underline">regulaminem pracowni</Link> oraz <span className="text-[#FFCC00]">Polityką prywatności RODO</span>. *</>}
              />
              <Checkbox
                testid="reg-marketing"
                checked={form.marketing_consent}
                onChange={(v) => upd("marketing_consent", v)}
                label="Wyrażam zgodę na otrzymywanie informacji marketingowych o działalności Śląski Maker Space drogą elektroniczną (zgoda dobrowolna, możesz wycofać w każdej chwili)."
              />
              <p className="text-xs text-white/45 leading-relaxed pt-1">
                Administrator danych: Śląski Maker Space, Plebiscytowa 15, 40-035 Katowice. Dane przetwarzane są zgodnie z RODO (Rozporządzenie UE 2016/679) w celu realizacji umowy oraz statystyki wejść.
                Konto główne wymaga pełnoletności — konto dla dziecka założysz po zalogowaniu z poziomu profilu rodzica.
              </p>
            </div>

            {err && <div className="text-red-400 text-sm" data-testid="wejscie-register-error">{err}</div>}

            <button type="submit" disabled={loading} data-testid="wejscie-register-submit"
              className="w-full bg-[#FFCC00] text-[#2A2A2A] font-display py-4 btn-brutal disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <UserPlus size={18}/> {loading ? "..." : "ZAŁÓŻ KONTO"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

function ForgotPasswordCard({ back }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/participants/forgot-password", { email });
      setMsg(data.detail);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Wystąpił błąd.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 md:p-12">
      <button onClick={back} className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFCC00] mb-4 font-mono-acc text-xs uppercase">
        <ArrowLeft size={14}/> Wróć do logowania
      </button>
      <div className="kicker mb-3">Odzyskiwanie dostępu</div>
      <h2 className="font-display text-white text-3xl uppercase mb-6">Resetowanie <span className="text-[#FFCC00]">hasła</span></h2>
      
      {msg ? (
        <div className="bg-[#2A2A2A] border-l-4 border-[#FFCC00] p-4 text-white/80 text-sm">
          {msg}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-white/60 text-sm mb-4">Podaj swój e-mail, na który wyślemy instrukcje resetowania hasła.</p>
          <Field label="Email" type="email" required value={email} onChange={setEmail} />
          {err && <div className="text-red-400 text-sm">{err}</div>}
          <button type="submit" disabled={loading}
            className="w-full bg-[#FFCC00] text-[#2A2A2A] font-display py-4 btn-brutal disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {loading ? "..." : "WYŚLIJ LINK"}
          </button>
        </form>
      )}
    </div>
  );
}

function Hub({ me, token, onCheckin, onAddChild, reload }) {
  const [target, setTarget] = useState(me.id);
  const all = [{ ...me, label: `${me.first_name} ${me.last_name} (Ja)` }, ...(me.children || []).map((c) => ({ ...c, label: `${c.first_name} ${c.last_name} (dziecko)` }))];

  return (
    <div data-testid="wejscie-hub">
      <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 md:p-10 mb-5">
        <div className="kicker mb-3">Cześć {me.first_name}!</div>
        <h2 className="font-display text-white text-3xl md:text-5xl uppercase mb-4">
          Rejestracja <span className="text-[#FFCC00]">wejścia</span>
        </h2>
        <p className="text-white/60 mb-6 text-sm">Wybierz uczestnika i potwierdź obecność. Sesja wygaśnie automatycznie po rejestracji.</p>

        {all.length > 1 && (
          <div className="space-y-2 mb-6" data-testid="wejscie-participant-list">
            {all.map((a) => (
              <label key={a.id} className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${target === a.id ? "border-[#FFCC00] bg-[#FFCC00]/5" : "border-[#3a3a3a] hover:border-white/40"}`} data-testid={`wejscie-pick-${a.id}`}>
                <input type="radio" checked={target === a.id} onChange={() => setTarget(a.id)} className="accent-[#FFCC00]"/>
                <div className="flex-1">
                  <div className="font-display text-white uppercase text-sm">{a.first_name} {a.last_name}</div>
                  <div className="font-mono-acc text-[10px] text-white/45 uppercase">{a.parent_id ? "Konto dziecka" : "Konto główne"}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <button
          onClick={() => onCheckin(target)}
          data-testid="wejscie-checkin-btn"
          className="w-full bg-[#FFCC00] text-[#2A2A2A] font-display text-xl sm:text-3xl md:text-5xl py-10 sm:py-14 md:py-16 px-6 btn-brutal inline-flex flex-col items-center justify-center gap-2"
        >
          <UserCheck size={48} className="mb-3"/>
          <span className="px-4">REJESTRACJA WEJŚCIA</span>
          <span className="text-sm sm:text-xl md:text-2xl opacity-90 px-4">UCZESTNIKA</span>
        </button>

        <AttendanceSection participantId={target} token={token} />
      </div>

      <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="kicker">Konta dzieci</div>
          <button onClick={onAddChild} data-testid="wejscie-add-child-btn" className="font-mono-acc text-xs uppercase text-[#FFCC00] hover:underline inline-flex items-center gap-2">
            <Plus size={14}/> Dodaj dziecko
          </button>
        </div>
        {(me.children || []).length === 0 ? (
          <p className="text-white/50 text-sm">Nie masz jeszcze przypisanych kont dziecięcych.</p>
        ) : (
          <ChildrenList children={me.children} reload={reload}/>
        )}
      </div>
    </div>
  );
}

function AttendanceSection({ participantId, token }) {
  const [data, setData] = useState({ total: 0, history: [] });
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/participants/attendance", {
        params: { participant_id: participantId, month, year },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(data);
    } catch (e) {
      console.error("Attendance fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [participantId, month, year, token]);

  useEffect(() => {
    if (participantId && token) fetchAttendance();
  }, [fetchAttendance, participantId, token]);

  const months = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];
  const years = [];
  const curYear = new Date().getFullYear();
  for (let y = curYear; y >= 2024; y--) years.push(y);

  return (
    <div className="mt-10 pt-10 border-t border-[#3a3a3a]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">Historia obecności</div>
          <div className="text-white text-xl">
            Suma wizyt: <span className="text-[#FFCC00] font-display uppercase">{data.total}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <select 
            value={year} 
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="bg-[#2A2A2A] border border-[#3a3a3a] text-white text-[11px] px-3 py-2 outline-none focus:border-[#FFCC00] font-mono-acc uppercase"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select 
            value={month} 
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="bg-[#2A2A2A] border border-[#3a3a3a] text-white text-[11px] px-3 py-2 outline-none focus:border-[#FFCC00] font-mono-acc uppercase"
          >
            {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-white/30 text-[10px] animate-pulse font-mono-acc uppercase">Pobieranie danych...</div>
      ) : (
        <div className="space-y-3">
           <div className="text-white/50 text-[10px] uppercase font-mono-acc">
             Wizyty w {months[month-1]} {year}: {data.history.length}
           </div>
           {data.history.length > 0 ? (
             <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.history.map((h, i) => (
                  <div key={i} className="bg-[#2A2A2A] border border-[#3a3a3a] p-3 flex items-center gap-3">
                    <Calendar size={14} className="text-[#FFCC00]"/>
                    <span className="font-mono-acc text-xs text-white/80">{h.date}</span>
                  </div>
                ))}
             </div>
           ) : (
             <div className="bg-[#2A2A2A]/50 border border-[#3a3a3a] border-dashed p-6 text-center text-white/30 text-xs italic">
               Brak zarejestrowanych wejść w tym okresie.
             </div>
           )}
        </div>
      )}
    </div>
  );
}

function ChildrenList({ children, reload }) {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const removeChild = async (cid) => {
    if (!window.confirm("Usunąć profil dziecka?")) return;
    await api.delete(`/participants/me/children/${cid}`, { headers: { Authorization: `Bearer ${token}` } });
    reload();
  };
  return (
    <ul className="space-y-2">
      {children.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-3 p-3 bg-[#2A2A2A] border border-[#3a3a3a]">
          <div>
            <div className="font-display text-white text-sm uppercase">{c.first_name} {c.last_name}</div>
            <div className="font-mono-acc text-[10px] text-white/45">ur. {c.dob}</div>
          </div>
          <button onClick={() => removeChild(c.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={14}/></button>
        </li>
      ))}
    </ul>
  );
}

function AddChildCard({ headers, back }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", dob: "", city: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/participants/me/children", form, { headers });
      back();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Błąd zapisu");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 md:p-10" data-testid="wejscie-add-child-card">
      <button onClick={back} className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFCC00] mb-4 font-mono-acc text-xs uppercase">
        <ArrowLeft size={14}/> Wróć
      </button>
      <div className="kicker mb-3">Nowe konto dziecięce</div>
      <h2 className="font-display text-white text-3xl uppercase mb-2">Dodaj <span className="text-[#FFCC00]">dziecko</span></h2>
      <p className="text-white/55 text-sm mb-6">Konto dziecka jest powiązane z Twoim profilem. Jako opiekun ponosisz odpowiedzialność i akceptujesz regulamin również w jego imieniu.</p>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Imię" required value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="child-firstname"/>
          <Field label="Nazwisko" required value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="child-lastname"/>
          <Field label="Data urodzenia" type="date" required value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="child-dob"/>
          <Field label="Miasto" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="child-city"/>
        </div>
        {err && <div className="text-red-400 text-sm">{err}</div>}
        <div className="flex items-start gap-2 text-xs text-white/55 leading-relaxed bg-[#2A2A2A] border border-[#3a3a3a] p-3">
          <ShieldCheck size={16} className="text-[#FFCC00] flex-shrink-0 mt-0.5"/>
          Akceptuję regulamin pracowni jako opiekun prawny i wyrażam zgodę na przetwarzanie danych dziecka w celu rejestracji wejść (RODO art. 6 ust. 1 lit. a w zw. z art. 8).
        </div>
        <button type="submit" disabled={loading} data-testid="child-submit"
          className="w-full bg-[#FFCC00] text-[#2A2A2A] font-display py-4 btn-brutal disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Plus size={18}/> {loading ? "..." : "DODAJ DZIECKO"}
        </button>
      </form>
    </div>
  );
}

function SuccessCard({ info, onContinue }) {
  return (
    <div className="bg-[#1F1F1F] border-2 border-[#FFCC00] p-10 md:p-16 text-center" data-testid="wejscie-success">
      <CheckCircle2 size={80} className="text-[#FFCC00] mx-auto mb-6"/>
      <div className="kicker mb-3 mx-auto">Wejście zarejestrowane</div>
      <h2 className="font-display text-white text-4xl md:text-6xl uppercase mb-4">
        Cześć,<br/><span className="text-[#FFCC00]">{info.participant_name}!</span>
      </h2>
      <p className="text-white/65 mb-8">Miłej pracy w pracowni 🛠</p>
      <div className="font-mono-acc text-xs text-white/40 uppercase">
        Zostaniesz automatycznie wylogowany za chwilę…
      </div>
      <button onClick={onContinue} className="mt-6 font-mono-acc text-xs uppercase text-[#FFCC00] hover:underline inline-flex items-center gap-2" data-testid="wejscie-success-continue">
        Wyloguj teraz <ArrowRight size={14}/>
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, testid }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  
  return (
    <div>
      <label className="font-mono-acc text-[10px] uppercase text-white/60">{label}{required && " *"}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type={isPassword ? (show ? "text" : "password") : type}
          required={required}
          minLength={minLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={isPassword ? (testid?.includes("reg") ? "new-password" : "current-password") : "off"}
          className={`w-full mt-2 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-4 py-3 focus:border-[#FFCC00] outline-none ${isPassword ? "pr-12" : ""}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(!show)}
            className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-white/40 hover:text-white"
            tabIndex="-1"
          >
            {show ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
    </div>
  );
}

function Checkbox({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer text-sm text-white/80 leading-relaxed">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#FFCC00] flex-shrink-0"
      />
      <span>{label}</span>
    </label>
  );
}
